import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../lib/api';
import {
  clearSharedAlbumClaimContext,
  readSharedAlbumClaimContext,
  trackSharedAlbumConversionEvent,
} from '../lib/sharedAlbumClaimContext';
import { readAlbumInvitationContext } from '../lib/albumInvitationContext';

async function completeClaim(authFlow) {
  const context = readSharedAlbumClaimContext();
  if (!context) return null;
  trackSharedAlbumConversionEvent('shared_album_claim_started', { ...context, auth_flow: authFlow });
  try {
    const response = await api.claimAlbumContribution(context.claim_token, authFlow);
    clearSharedAlbumClaimContext();
    trackSharedAlbumConversionEvent('shared_album_claim_succeeded', { ...context, auth_flow: authFlow });
    return { response: response.data, context };
  } catch (error) {
    trackSharedAlbumConversionEvent('shared_album_claim_failed', { ...context, auth_flow: authFlow });
    throw error;
  }
}

export default function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup, demoLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isClaimContinuation = location.search.includes('claim=shared-album');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(email, password, fullName);
      const invitation = readAlbumInvitationContext();
      if (isClaimContinuation) {
        trackSharedAlbumConversionEvent('shared_album_account_created', readSharedAlbumClaimContext() || {});
      }
      const claim = await completeClaim('signup');
      if (claim) {
        toast.success(claim.response?.status === 'already_claimed' ? 'Your contribution is already linked.' : 'Your contribution is linked to your new account.');
        navigate(claim.context?.share_slug ? `/a/${claim.context.share_slug}` : '/dashboard');
      } else {
        toast.success('Account created successfully!');
        navigate(invitation?.share_slug ? `/a/${invitation.share_slug}` : '/dashboard');
      }
    } catch (error) {
      // Check if it's an email confirmation message
      const errorMessage = error.message || error.response?.data?.detail || 'Signup failed';
      const statusCode = error.response?.status;
      
      // Network error check
      if (isClaimContinuation && statusCode === 410) {
        clearSharedAlbumClaimContext();
        toast.error('That contribution claim expired. Your photos are still pending owner review.', { duration: 6000 });
        navigate('/dashboard');
      } else if (isClaimContinuation && (statusCode === 404 || statusCode === 409 || statusCode === 403)) {
        clearSharedAlbumClaimContext();
        toast.error('We could not link that contribution. Your photos remain pending owner review.', { duration: 6000 });
        navigate('/dashboard');
      } else if (error.code === 'ERR_NETWORK' || !error.response) {
        toast.error('Network error. Please check your connection and try again.', { duration: 6000 });
      } else if (errorMessage.includes('check your email') || errorMessage.includes('confirm your account') || errorMessage.includes('Check email')) {
        toast.success('Check your email to confirm your account!', { duration: 6000 });
        // Redirect to login after showing the message
        setTimeout(() => navigate('/login'), 2000);
      } else if (statusCode === 429 || errorMessage.includes('rate limit') || errorMessage.includes('Too many')) {
        // Rate limiting - show warning with longer duration
        toast.error('Too many signup attempts. Please try Demo Mode or wait a few minutes.', { duration: 8000 });
      } else if (errorMessage.includes('already registered') || errorMessage.includes('already exists')) {
        // User already exists - suggest login
        toast.error('This email is already registered. Try logging in instead.', { duration: 6000 });
        setTimeout(() => navigate('/login'), 2000);
      } else if (errorMessage.includes('CAPTCHA')) {
        toast.error('CAPTCHA verification needed. Please use Demo Mode to explore the app.', { duration: 8000 });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async () => {
    setLoading(true);
    try {
      await demoLogin();
      toast.success('Welcome to the demo! Exploring with sample data.');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Demo login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0A0A0A' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-[#141414] rounded-[24px] shadow-card p-8">
          <div className="text-center mb-8">
            <h1 className="font-heading text-4xl text-white mb-2" data-testid="signup-heading">Create Account</h1>
            <p className="text-stone-300" data-testid="signup-subheading">Start preserving your memories</p>
            {isClaimContinuation ? (
              <p className="text-sm text-[#D4A574] mt-2">Create your account to preserve your shared album contribution.</p>
            ) : null}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-stone-200 mb-2" htmlFor="fullName">
                Full Name
              </label>
              <input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full bg-[#0F0F0F] border-transparent focus:border-[#2A2A2A] focus:ring-0 rounded-xl py-3 px-4 text-white"
                placeholder="John Doe"
                required
                data-testid="signup-name-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-200 mb-2" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#0F0F0F] border-transparent focus:border-[#2A2A2A] focus:ring-0 rounded-xl py-3 px-4 text-white"
                placeholder="your@email.com"
                required
                data-testid="signup-email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-200 mb-2" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#0F0F0F] border-transparent focus:border-[#2A2A2A] focus:ring-0 rounded-xl py-3 px-4 text-white"
                placeholder="••••••••"
                required
                minLength={6}
                data-testid="signup-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary"
              data-testid="signup-submit-button"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          {/* Demo Login Option */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#2A2A2A]"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-[#141414] text-stone-400">Or try without signing up</span>
              </div>
            </div>
            
            <button
              onClick={handleDemoLogin}
              disabled={loading}
              className="mt-4 w-full bg-[#1C1C1C] text-stone-200 rounded-full px-6 py-3 font-medium hover:bg-[#2A2A2A] transition-all"
              data-testid="demo-login-button"
            >
              {loading ? 'Loading demo...' : '🎨 Try Demo Mode'}
            </button>
            <p className="text-xs text-stone-400 text-center mt-2">
              Explore with sample data • No account needed
            </p>
          </div>

          <div className="mt-6 text-center">
            <p className="text-stone-300">
              Already have an account?{' '}
              <Link to={isClaimContinuation ? '/login?claim=shared-album' : '/login'} className="text-primary font-medium hover:underline" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
